import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import useCompanyState from './company.state';

export interface ISanction {
  warning_clause: string;
  fee: string;
}

export interface IPunishment {
  id?: number | string;
  tenant_id?: number | string;
  name?: string;
  value?: string;
  category?: string;
  category_other?: string;
  severity_level?: string;
  definition_warning?: string;
  sanction_description?: string;
  sanctions?: ISanction[];
  description?: string;
  is_published?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachment_file?: any;
  created_at?: string;
  updated_at?: string;
  fileUrl?: string;
}

interface PaginationParams {
  page?: number;
  page_size?: number;
  order?: 'asc' | 'desc';
  order_by?: string;
  category?: string;
  severity_level?: string;
  published?: boolean;
  search?: string;
}

interface PaginatedData {
  content: IPunishment[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface PunishmentState {
  punishments: IPunishment[];
  paginatedData: PaginatedData | null;
  selectedPunishment: IPunishment | null;
  isLoading: boolean;
  error: unknown;

  createPunishment: (tenantId: string | number, payload: Partial<IPunishment>) => Promise<void>;
  getPunishments: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  getPunishmentDetail: (tenantId: string | number, id: string | number) => Promise<IPunishment | void>;
  updatePunishment: (tenantId: string | number, id: string | number, payload: Partial<IPunishment>) => Promise<void>;
  deletePunishment: (tenantId: string | number, id: string | number) => Promise<void>;
  clearPunishments: () => void;
  setPunishment: (punishments: IPunishment[]) => void;
  bulkPublishPunishment: (tenantId: string | number, isPublished: boolean) => Promise<void>;
}

const usePunishmentState = create<PunishmentState>((set, get) => ({
  punishments: [],
  paginatedData: null,
  selectedPunishment: null,
  isLoading: false,
  error: null,

  setPunishment: punishments => set({ punishments }),
  clearPunishments: () => set({ punishments: [] }),

  createPunishment: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const { uploadCompanyMediaDirect } = await import('@/hooks/useCompanyProfile');

      if (payload.attachment_file) {
        const file = Array.isArray(payload.attachment_file) ? payload.attachment_file[0] : payload.attachment_file;

        const uploadRes = (await uploadCompanyMediaDirect(file, tenantId, 'punishment Attachment')) as any;
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getPunishments(tenantId);
      toast.success('Punishment berhasil dibuat');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updatePunishment: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const { uploadCompanyMediaDirect } = await import('@/hooks/useCompanyProfile');

      const attachment = payload.attachment_file;
      const firstFile = Array.isArray(attachment)
        ? attachment[0]
        : attachment instanceof File
        ? attachment
        : attachment?.file || attachment?.originFileObj;

      const actualFile =
        firstFile instanceof File
          ? firstFile
          : firstFile?.originFileObj instanceof File
          ? firstFile.originFileObj
          : firstFile?.file instanceof File
          ? firstFile.file
          : null;

      const hasNewFile = !!actualFile;

      if (hasNewFile) {
        const uploadRes = (await uploadCompanyMediaDirect(actualFile, tenantId, 'punishment Attachment')) as any;
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      } else {
        delete payload.attachment_file;
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);

      set(state => ({
        punishments: state.punishments.map(rw => (rw.id === id ? { ...rw, ...data.data } : rw)),
        selectedPunishment: data.data,
      }));

      toast.success('Punishment berhasil diperbarui');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getPunishments: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const punishmentsData = (data.data?.content || data.data || []).map((punishment: IPunishment) => {
        let fileUrl: string | null = null;
        if (punishment.attachment_file) {
          try {
            const files = JSON.parse(punishment.attachment_file);
            if (files.length > 0 && files[0].url) {
              fileUrl = files[0].url;
            }
          } catch {
            fileUrl = null;
          }
        }
        return { ...punishment, fileUrl };
      });

      // Set both punishments (for backward compatibility) and paginatedData
      set({
        punishments: punishmentsData,
        paginatedData: {
          content: punishmentsData,
          totalElements: data.data?.totalElements || punishmentsData.length,
          totalPages: data.data?.totalPages || 1,
          size: data.data?.size || params?.page_size || 10,
          number: data.data?.number || params?.page || 0,
        },
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getPunishmentDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedPunishment: data.data });
      return data.data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  deletePunishment: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        punishments: state.punishments.filter(rw => rw.id !== id),
      }));
      toast.success('Punishment berhasil dihapus');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishPunishment: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/bulk-publish`;
      const { data } = await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        punishments: state.punishments.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(
        isPublished ? 'Semua punishment berhasil dipublikasikan' : 'Semua punishment berhasil dibatalkan publikasinya',
      );
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default usePunishmentState;
